import { useCallback, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { TEMPLATE_CATALOG } from '@/components/event-viewer/templates'
import { useToast } from '@/components/play'
import { useAuth } from '@/state/AuthContext'
import type { EventResource } from '@/types/events'
import { api } from '@/util/api'
import { DashboardHeroSection } from './dashboard/DashboardHeroSection'
import { StarterKitsSection } from './dashboard/StarterKitsSection'
import { deriveEventNameFromPrompt } from './dashboard/eventName'
import type { AgentMode } from './dashboard/types'

export function AiProducerPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [agentMode, setAgentMode] = useState<AgentMode>('design')
  const [promptValue, setPromptValue] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const promptInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()

  const firstName = user?.attributes?.first_name || 'there'

  const handlePromptSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault()
      if (!promptValue.trim() || isSubmitting) return

      setIsSubmitting(true)
      try {
        const eventName = deriveEventNameFromPrompt(promptValue)
        const res = await api.post<{ data: EventResource }>('/events', {
          event: { name: eventName },
        })

        if (res.data?.id) {
          navigate(`/events/${res.data.id}/canvas`, {
            state: { initialPrompt: promptValue, mode: agentMode },
          })
        }
      } catch (err: any) {
        toast({
          title: 'Event not created',
          description: err?.response?.data?.error?.message || 'Check your connection and try again.',
          tone: 'error',
        })
      } finally {
        setIsSubmitting(false)
      }
    },
    [agentMode, isSubmitting, navigate, promptValue, toast],
  )

  const handleStarterKitSelect = useCallback((templateId: string) => {
    const template = TEMPLATE_CATALOG.find((item) => item.id === templateId)
    if (!template) return

    setPromptValue(
      `Create a ${template.name.toLowerCase()} event page with ${template.features.slice(0, 3).join(', ').toLowerCase()}`,
    )
    setAgentMode('design')
    promptInputRef.current?.focus()
  }, [])

  return (
    <div className="relative min-h-screen bg-transparent">
      <div className="app-page max-w-6xl space-y-8">
        <DashboardHeroSection
          firstName={firstName}
          agentMode={agentMode}
          setAgentMode={setAgentMode}
          promptValue={promptValue}
          setPromptValue={setPromptValue}
          isSubmitting={isSubmitting}
          promptInputRef={promptInputRef}
          handlePromptSubmit={handlePromptSubmit}
        />
        <StarterKitsSection handleStarterKitSelect={handleStarterKitSelect} />
      </div>
    </div>
  )
}
